import { cn } from "@/lib/utils";
import { createElement, type ReactNode } from "react";

type Tone = "default" | "inverse";

type EyebrowProps = {
  children: ReactNode;
  tone?: Tone;
  align?: "left" | "center";
  showDot?: boolean;
  className?: string;
};

export function Eyebrow({
  children,
  tone = "default",
  align = "left",
  showDot = true,
  className,
}: EyebrowProps) {
  return (
    <p
      className={cn(
        "flex items-center gap-3 uppercase text-[11px] tracking-[0.22em] leading-none",
        align === "center" && "justify-center",
        tone === "inverse" ? "text-background/70" : "text-brand-800",
        className,
      )}
    >
      {showDot && (
        <span
          aria-hidden
          className={cn(
            "size-1.5 rounded-full shrink-0",
            tone === "inverse" ? "bg-background/55" : "bg-brand-800",
          )}
        />
      )}
      <span>{children}</span>
    </p>
  );
}

type TitleVariant = "hero" | "section" | "sub";

const TITLE_VARIANT_CLASSES: Record<TitleVariant, string> = {
  hero: "text-[clamp(2.5rem,6.4vw,5.25rem)] leading-[1.02] tracking-[-0.035em] font-normal",
  section:
    "text-[clamp(1.85rem,3.6vw,3rem)] leading-[1.06] tracking-[-0.035em] font-normal",
  sub: "text-[clamp(1.4rem,2.4vw,1.85rem)] leading-[1.1] tracking-[-0.025em] font-normal",
};

const DEFAULT_TAG: Record<TitleVariant, "h1" | "h2" | "h3"> = {
  hero: "h1",
  section: "h2",
  sub: "h3",
};

type TitleProps = {
  children: ReactNode;
  variant?: TitleVariant;
  as?: "h1" | "h2" | "h3";
  accent?: string;
  tone?: Tone;
  align?: "left" | "center";
  className?: string;
};

export function Title({
  children,
  variant = "section",
  as,
  accent,
  tone = "default",
  align = "left",
  className,
}: TitleProps) {
  const tag = as ?? DEFAULT_TAG[variant];

  return createElement(
    tag,
    {
      className: cn(
        TITLE_VARIANT_CLASSES[variant],
        align === "center" && "text-center",
        tone === "inverse" ? "text-background" : "text-foreground",
        className,
      ),
    },
    children,
    accent && (
      <>
        {" "}
        <span
          className={cn(
            "font-serif italic font-normal tracking-[-0.02em]",
            tone === "inverse" ? "text-background" : "text-brand-800",
          )}
        >
          {accent}
        </span>
      </>
    ),
  );
}
